import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Box,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    Stepper,
    Step,
    StepLabel,
    IconButton,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CloseIcon from '@mui/icons-material/Close'

import { useAddManualDesmosActMutation } from '@/core/services/consultation'
import { useSnackbar } from 'notistack'
import SimpleDesmosActSelectionDialog, { SimpleActDto } from './SimpleDesmosActSelectionDialog'

interface MassBillingDialogProps {
    open: boolean
    onClose: () => void
    consultationIds: string[]
    consultations: Array<{
        _id: string
        patient: { userInfo: { name: string; surname: string } }
        medecin?: { userInfo: { name: string; surname: string }; cps?: string }
    }>
}

type ProcessingStatus = 'pending' | 'processing' | 'success' | 'error'

interface ConsultationProcessingState {
    consultationId: string
    patientName: string
    status: ProcessingStatus
    error?: string
    desmosActId?: string
}

export default function MassBillingDialog({
    open,
    onClose,
    consultationIds,
    consultations
}: MassBillingDialogProps) {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const [addManualDesmosAct] = useAddManualDesmosActMutation()
    const isMountedRef = useRef(true)
    const finalCountsRef = useRef({ success: 0, error: 0 })

    const [showActSelectionDialog, setShowActSelectionDialog] = useState(true)
    const [selectedAct, setSelectedAct] = useState<SimpleActDto | null>(null)
    const [isProcessing, setIsProcessing] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [processingStates, setProcessingStates] = useState<ConsultationProcessingState[]>([])

    useEffect(() => {
        isMountedRef.current = true

        if (open && consultationIds.length > 0) {
            // Reset final counts
            finalCountsRef.current = { success: 0, error: 0 }

            // Initialize processing states
            const initialStates = consultationIds.map(id => {
                const consultation = consultations.find(c => c._id === id)
                return {
                    consultationId: id,
                    patientName: consultation
                        ? `${consultation.patient.userInfo.name} ${consultation.patient.userInfo.surname}`
                        : 'Unknown Patient',
                    status: 'pending' as ProcessingStatus
                }
            })
            setProcessingStates(initialStates)
            setCurrentIndex(0)
            setIsProcessing(false)
            setShowActSelectionDialog(true)
            setSelectedAct(null)
        }

        // Cleanup on unmount - prevent state updates
        return () => {
            isMountedRef.current = false
        }
    }, [open, consultationIds]) // Removed consultations to prevent re-initialization during processing

    const handleActSelected = (actValues: SimpleActDto) => {
        setSelectedAct(actValues)
        setShowActSelectionDialog(false)
        // Start processing immediately
        startMassProcessing(actValues)
    }

    const startMassProcessing = async (actValues: SimpleActDto) => {
        if (!isMountedRef.current) return

        setIsProcessing(true)

        for (let i = 0; i < consultationIds.length; i++) {
            if (!isMountedRef.current) break // Stop if component unmounted

            const consultationId = consultationIds[i]

            if (isMountedRef.current) {
                setCurrentIndex(i)

                // Update status to processing
                setProcessingStates(prev => prev.map((state, idx) =>
                    idx === i ? { ...state, status: 'processing' } : state
                ))
            }

            try {
                // Create the manual act for this consultation
                // Practitioner info will be taken from consultation by backend
                await addManualDesmosAct({
                    consultationId,
                    actData: {
                        libelle: actValues.libelle,
                        date_realisation: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
                        honoraire: actValues.honoraire,
                        id_transposition: actValues.id_transposition,
                        supplement: actValues.supplement || '0',
                        modalite_complementaire: {
                            code_situation_parcours_de_soin: null,
                            nom_medecin_parcours_de_soins: null,
                            prenom_medecin_parcours_de_soins: null,
                            code_majoration_ou_modificateurs: null,
                            code_justif_exoneration_acte: null,
                            code_qualificatif_depence: null,
                            code_association: null,
                            is_remboursement_exceptionnel: null,
                            is_ald: null,
                            taux_abattement: null,
                        }
                    },
                }).unwrap()

                if (isMountedRef.current) {
                    // Update status to success
                    setProcessingStates(prev => prev.map((state, idx) =>
                        idx === i ? { ...state, status: 'success' } : state
                    ))
                    finalCountsRef.current.success++
                }
            } catch (error: any) {
                console.error(`Error creating act for consultation ${consultationId}:`, error)

                if (isMountedRef.current) {
                    // Update status to error
                    setProcessingStates(prev => prev.map((state, idx) =>
                        idx === i ? {
                            ...state,
                            status: 'error',
                            error: error.message || 'Unknown error'
                        } : state
                    ))
                    finalCountsRef.current.error++
                }
            }
        }

        if (isMountedRef.current) {
            setIsProcessing(false)

            // Show completion message using final counts
            const { success: successCount, error: errorCount } = finalCountsRef.current

            if (errorCount === 0) {
                enqueueSnackbar(t('notification.desmos.massBilling.success', { count: successCount }), { variant: 'success' })
            } else {
                enqueueSnackbar(t('notification.desmos.massBilling.partial', { successCount, errorCount }), { variant: 'warning' })
            }
        }
    }

    const handleClose = () => {
        if (!isProcessing) {
            onClose()
        }
    }

    const getStatusIcon = (status: ProcessingStatus) => {
        switch (status) {
            case 'success':
                return <CheckCircleIcon color="success" />
            case 'error':
                return <ErrorIcon color="error" />
            case 'processing':
                return <HourglassEmptyIcon color="primary" />
            default:
                return <HourglassEmptyIcon color="disabled" />
        }
    }

    const progress = consultationIds.length > 0
        ? ((currentIndex + 1) / consultationIds.length) * 100
        : 0

    return (
        <>
            {/* Act Selection Dialog */}
            <SimpleDesmosActSelectionDialog
                open={open && showActSelectionDialog}
                onClose={handleClose}
                onSelectAct={handleActSelected}
                title={t('page.network.desmos.massBilling.title')}
                description={t('page.network.desmos.massBilling.description', { count: consultationIds.length })}
            />

            {/* Processing Progress Dialog */}
            <Dialog
                open={open && !showActSelectionDialog}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown={isProcessing}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {t('page.network.desmos.massBilling.progressTitle')}
                        </Typography>
                        {!isProcessing && (
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={3}>
                        {/* Overall Progress */}
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">
                                    {t('page.network.desmos.massBilling.overallProgress')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {Math.min(currentIndex + 1, consultationIds.length)} / {consultationIds.length}
                                </Typography>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>

                        {/* Selected Act Info */}
                        {selectedAct && (
                            <Alert severity="info">
                                <Typography variant="body2">
                                    <strong>{t('page.network.desmos.massBilling.billingAct')}:</strong> {selectedAct.libelle} ({selectedAct.id_transposition}) - {selectedAct.honoraire}€
                                </Typography>
                            </Alert>
                        )}

                        {/* Processing List */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('page.network.desmos.massBilling.consultations')}:
                            </Typography>
                            <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                                {processingStates.map((state, index) => (
                                    <ListItem
                                        key={state.consultationId}
                                        sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            backgroundColor: theme => {
                                                if (state.status === 'success') {
                                                    return theme.palette.mode === 'light' ? 'rgba(46, 125, 50, 0.08)' : 'rgba(102, 187, 106, 0.16)'
                                                }
                                                if (state.status === 'error') {
                                                    return theme.palette.mode === 'light' ? 'rgba(211, 47, 47, 0.08)' : 'rgba(244, 67, 54, 0.16)'
                                                }
                                                if (state.status === 'processing') {
                                                    return theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(66, 165, 245, 0.16)'
                                                }
                                                return 'transparent'
                                            }
                                        }}
                                    >
                                        <ListItemIcon>{getStatusIcon(state.status)}</ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2">
                                                    {state.patientName}
                                                </Typography>
                                            }
                                            secondary={
                                                <>
                                                    {state.status === 'success' && (
                                                        <Typography variant="caption" color="success.dark">
                                                            {t('page.network.desmos.massBilling.successfullyBilled')}
                                                            {state.desmosActId && ` - ID: ${state.desmosActId}`}
                                                        </Typography>
                                                    )}
                                                    {state.status === 'error' && (
                                                        <Typography variant="caption" color="error.dark">
                                                            {t('page.network.desmos.details.error')}: {state.error}
                                                        </Typography>
                                                    )}
                                                    {state.status === 'processing' && (
                                                        <Typography variant="caption" color="primary">
                                                            {t('page.network.desmos.massBilling.processing')}
                                                        </Typography>
                                                    )}
                                                    {state.status === 'pending' && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {t('page.network.desmos.massBilling.waiting')}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>

                        {/* Summary */}
                        {!isProcessing && processingStates.length > 0 && (
                            <Alert severity={finalCountsRef.current.error === 0 ? 'success' : 'warning'}>
                                <Typography variant="body2">
                                    <strong>{t('page.network.desmos.massBilling.summary')}:</strong>{' '}
                                    {finalCountsRef.current.success} {t('common.successful')},{' '}
                                    {finalCountsRef.current.error} {t('common.failed')}
                                </Typography>
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={isProcessing}>
                        {isProcessing ? t('page.network.desmos.massBilling.processing') : t('page.network.desmos.massBilling.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

